// Контекстная подсказка «?» рядом с полем или заголовком секции.
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Box, IconButton, Popover, Typography } from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { usePublicUiLocale } from '../contexts/PublicUiLocaleContext';

const ARIA_LABEL = { uk: 'Довідка', en: 'Help' };

function HelpHint({ title, text, size = 'small', sx }) {
  const { lang } = usePublicUiLocale();
  const [anchorEl, setAnchorEl] = useState(null);

  if (!text) return null;

  const paragraphs = Array.isArray(text) ? text.filter(Boolean) : [text];

  return (
    <>
      <IconButton
        size={size}
        aria-label={ARIA_LABEL[lang] || ARIA_LABEL.en}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{ color: 'text.secondary', '&:hover': { color: 'text.primary' }, ...sx }}
      >
        <HelpOutlineIcon fontSize={size === 'large' ? 'medium' : 'small'} />
      </IconButton>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            maxWidth: 360,
            p: 1.5,
            bgcolor: 'background.paper',
            backgroundImage: 'none',
            border: 1,
            borderColor: 'divider',
          },
        }}
      >
        {title && (
          <Typography variant="subtitle2" sx={{ color: 'text.primary', mb: 0.5 }}>
            {title}
          </Typography>
        )}
        {paragraphs.map((paragraph, idx) => (
          <Typography
            key={idx}
            variant="body2"
            sx={{ color: 'text.secondary', mb: idx === paragraphs.length - 1 ? 0 : 1 }}
          >
            {paragraph}
          </Typography>
        ))}
      </Popover>
    </>
  );
}

HelpHint.propTypes = {
  title: PropTypes.string,
  text: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  sx: PropTypes.object,
};

// Оборачивает поле формы, чтобы «?» встал справа от него и не ломал fullWidth.
// Иконка выравнивается по верху поля: для высоких multiline это верный край,
// для обычного поля — его середина. offsetTop нужен, когда поле само задаёт
// верхний отступ (margin="normal" — это mt: 2, значит offsetTop={3.5}).
export function HelpField({ hint, hintTitle, offsetTop = 1.5, children }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {children}
      </Box>
      <HelpHint title={hintTitle} text={hint} sx={{ mt: offsetTop }} />
    </Box>
  );
}

HelpField.propTypes = {
  hint: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  hintTitle: PropTypes.string,
  offsetTop: PropTypes.number,
  children: PropTypes.node,
};

export default HelpHint;
